import { useState, useEffect, useMemo } from "react";
import RuleCard from "./cards/RuleCard";
import Skeleton from 'react-loading-skeleton';
import QuestionService from "../../../../services/httpServices/question.service";
import { IRule } from "../../../../interfaces/rule";
import { IBlock } from "../../../../interfaces/block";
import { Select, MenuItem, FormControl, InputLabel } from '@mui/material';

const sortOptions = [
  { value: 'questions', label: 'Preguntas totales' },
  { value: 'reports',   label: 'Reportes totales' },
  { value: 'tests',     label: 'Test totales' },
  { value: 'accuracy',  label: 'Porcentaje de acierto' },
  { value: 'ratio',     label: 'Relación preguntas/test' },
];

interface ListRulesProps {
  block: IBlock;
}

export default function ListRules(props: ListRulesProps): JSX.Element {
  const { block } = props;
  const [normativas, setNormativas] = useState<IRule[]>([]);
  const [sortField, setSortField] = useState<string>(sortOptions[0].value);
  const [showBlockName, setShowBlockName] = useState<boolean>(false);
  const { getStandardsSummary, getMultipleStandardsSummaries } = useMemo(() => QuestionService(), []);

  useEffect(() => {
    const fetchStandardsSummary = async () => {
      if (block && block.name) {
        try {
          setNormativas([]);

          if (block.includes && block.includes.length > 0) {
            setShowBlockName(true);
            const summaries = await getMultipleStandardsSummaries(block.includes);
            setNormativas(summaries);
          }
          else {
            setShowBlockName(false);
            const summary = await getStandardsSummary(block.name);
            setNormativas(summary);
          }
        }
        catch (error) {
          setNormativas([]);
          console.error("Error fetching standards summary:", error);
        }
      }
    };

    fetchStandardsSummary();
  }, [block, getStandardsSummary, getMultipleStandardsSummaries]);


  const sortedNormativas = useMemo(() => {
    return [...normativas].sort((a, b) => {
      let aValue: number;
      let bValue: number;

      switch (sortField) {
        case 'questions':
          aValue = a.questions.total;
          bValue = b.questions.total;
          break;
        case 'reports':
          aValue = a.reports.total;
          bValue = b.reports.total;
          break;
        case 'tests':
          aValue = a.tests.total;
          bValue = b.tests.total;
          break;
        case 'accuracy':
          aValue = a.total_correct + a.total_incorrect > 0 ? a.total_correct / (a.total_correct + a.total_incorrect) : 0;
          bValue = b.total_correct + b.total_incorrect > 0 ? b.total_correct / (b.total_correct + b.total_incorrect) : 0;
          break;
        case 'ratio':
          aValue = a.tests.total > 0 ? a.questions.total / a.tests.total : 0;
          bValue = b.tests.total > 0 ? b.questions.total / b.tests.total : 0;
          break;
        default:
          aValue = 0;
          bValue = 0;
      }
      return bValue - aValue;
    });
  }, [normativas, sortField]);

  return (
    <>
      <FormControl variant="outlined" fullWidth>
        <InputLabel id="sort-label">Ordenar por</InputLabel>
        <Select
          labelId="sort-label"
          value={sortField}
          label="Ordenar por"
          onChange={(e) => setSortField(e.target.value)}
        >
          {sortOptions.map((opt) => (
            <MenuItem key={opt.value} value={opt.value}>
              {opt.label}
            </MenuItem>
          ))}
        </Select>
      </FormControl>

      <div className="grid grid-cols-1 gap-6 mt-6">
        {normativas.length === 0 ? (
          <Skeleton count={4} height={200} />
        ) : sortedNormativas.length === 0 ? (
          <p className="col-span-1 text-center text-gray-500">
            No hay normativas para mostrar
          </p>
        ) : (
          sortedNormativas.map((rule, index) => (
            <RuleCard rule={rule} key={index} showBlockName={showBlockName} />
          ))
        )}
      </div>
    </>
  );
}
